import { COUNTED_ENTITIES_LABEL } from './entity-counting'
import { UTILITIES_ENABLED } from '@/lib/features'

export type BillingInterval = 'month' | 'year'

export type AIFeatureAccess = 'trial' | 'limited' | 'unlimited' | false

export const TIER_LIMITS = {
  free: {
    maxProperties: 1,
    maxEntities: 50, // items + parts + utilities + open tasks; see lib/entity-counting.ts
    maxStorageBytes: 0.5 * 1024 * 1024 * 1024, // 0.5 GB
    maxChatMessagesPerMonth: 20,
    features: {
      imageUpload: true,
      documentUpload: true,
      exportData: false,
      aiMessages: 'trial' as AIFeatureAccess,
      invoiceScanner: 'trial' as AIFeatureAccess,
      itemDetection: 'trial' as AIFeatureAccess,
    },
  },
  pro: {
    maxProperties: 1,
    maxEntities: 500,
    maxStorageBytes: 5 * 1024 * 1024 * 1024, // 5 GB
    maxChatMessagesPerMonth: 200,
    features: {
      imageUpload: true,
      documentUpload: true,
      exportData: true,
      aiMessages: 'limited' as AIFeatureAccess,
      invoiceScanner: 'limited' as AIFeatureAccess,
      itemDetection: 'limited' as AIFeatureAccess,
    },
  },
  max: {
    maxProperties: 3,
    maxEntities: 5000,
    maxStorageBytes: 20 * 1024 * 1024 * 1024, // 20 GB
    maxChatMessagesPerMonth: 1000,
    features: {
      imageUpload: true,
      documentUpload: true,
      exportData: true,
      aiMessages: 'unlimited' as AIFeatureAccess,
      invoiceScanner: 'unlimited' as AIFeatureAccess,
      itemDetection: 'unlimited' as AIFeatureAccess,
    },
  },
  unlimited: {
    maxProperties: 100,
    maxEntities: 100000,
    maxStorageBytes: 100 * 1024 * 1024 * 1024, // 100 GB
    maxChatMessagesPerMonth: 100000,
    features: {
      imageUpload: true,
      documentUpload: true,
      exportData: true,
      aiMessages: 'unlimited' as AIFeatureAccess,
      invoiceScanner: 'unlimited' as AIFeatureAccess,
      itemDetection: 'unlimited' as AIFeatureAccess,
    },
  },
} as const

export type SubscriptionTier = keyof typeof TIER_LIMITS

export function getTierLimits(tier: SubscriptionTier) {
  return TIER_LIMITS[tier]
}

export function canExceedLimit(
  tier: SubscriptionTier,
  limitType: 'properties' | 'entities' | 'storage',
  currentValue: number
): { allowed: boolean; limit: number; message?: string } {
  const limits = getTierLimits(tier)
  const displayName = getTierDisplayName(tier)

  switch (limitType) {
    case 'properties':
      return {
        allowed: currentValue < limits.maxProperties,
        limit: limits.maxProperties,
        message:
          currentValue >= limits.maxProperties
            ? `You've reached the ${displayName} plan limit of ${limits.maxProperties
            } ${limits.maxProperties === 1 ? 'property' : 'properties'
            }. Upgrade to add more.`
            : undefined,
      }
    case 'entities':
      return {
        allowed: currentValue < limits.maxEntities,
        limit: limits.maxEntities,
        message:
          currentValue >= limits.maxEntities
            ? `${entityLimitMessage(tier)} Upgrade to add more.`
            : undefined,
      }
    case 'storage':
      return {
        allowed: currentValue < limits.maxStorageBytes,
        limit: limits.maxStorageBytes,
        message:
          currentValue >= limits.maxStorageBytes
            ? `You've reached the ${displayName} plan storage limit. Upgrade for more storage.`
            : undefined,
      }
  }
}

/**
 * Why a create was refused, naming what the limit counts:
 * "You've reached the Essential plan limit of 50 active items, parts, tasks, and utilities."
 * With `available`/`requested` for a batch that only partly fits:
 * "This would go over the Essential plan limit of 50 active items, parts, tasks, and utilities: 3 left, 5 requested."
 */
export function entityLimitMessage(tier: SubscriptionTier, available = 0, requested = 1): string {
  const limit = `${getTierDisplayName(tier)} plan limit of ${TIER_LIMITS[tier].maxEntities.toLocaleString()} ${COUNTED_ENTITIES_LABEL}`
  return available > 0
    ? `This would go over the ${limit}: ${available} left, ${requested} requested.`
    : `You've reached the ${limit}.`
}

export function formatStorageSize(bytes: number): string {
  const gb = bytes / (1024 * 1024 * 1024)
  if (gb >= 1) return `${gb.toFixed(0)} GB`

  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${(mb / 1000 - 0.01).toFixed(1)} GB`

  const kb = bytes / 1024
  return `${kb.toFixed(2)} KB`
}

export function getTierDisplayName(tier: SubscriptionTier): string {
  const names: Record<SubscriptionTier, string> = {
    free: 'Essential',
    pro: 'Household',
    max: 'Estate',
    unlimited: 'Portfolio',
  }
  return names[tier]
}

export function getTierPrice(tier: SubscriptionTier, interval: BillingInterval = 'month'): number {
  const monthly: Record<SubscriptionTier, number> = {
    free: 0,
    pro: 5,
    max: 15,
    unlimited: 25,
  }
  if (interval === 'year') {
    // ~20% annual discount, rounded to whole dollars
    const annual: Record<SubscriptionTier, number> = {
      free: 0,
      pro: 48,
      max: 144,
      unlimited: 0, // Portfolio is "Contact us"
    }
    return annual[tier]
  }
  return monthly[tier]
}

/** Get the effective monthly price for annual billing (for display: "$X/mo") */
export function getAnnualMonthlyPrice(tier: SubscriptionTier): number {
  const annualTotal = getTierPrice(tier, 'year')
  return Math.round(annualTotal / 12)
}

/** Whether a tier supports self-serve checkout */
export function isSelfServeTier(tier: SubscriptionTier): boolean {
  return tier !== 'free' && tier !== 'unlimited'
}

/** Ordered list of tiers for display purposes */
export const TIER_ORDER: SubscriptionTier[] = ['free', 'pro', 'max', 'unlimited']

export type FeatureChip = {
  label: string
  type: 'gradient' | 'muted-gradient' | 'trial'
}

export type TierFeature = {
  text: string
  chip?: FeatureChip
  muted?: boolean
  /** Tooltip text, shown behind an info icon. */
  info?: string
}

/** Map AI feature access level to a chip */
function aiAccessChip(access: AIFeatureAccess): FeatureChip | undefined {
  switch (access) {
    case 'trial':
      return { label: 'Trial', type: 'trial' }
    case 'limited':
      return { label: 'Limited', type: 'gradient' }
    case 'unlimited':
      return { label: 'Unlimited', type: 'gradient' }
    default:
      return undefined
  }
}

/** Feature descriptions for each tier (for pricing cards) */
export function getTierFeatures(tier: SubscriptionTier): TierFeature[] {
  const limits = TIER_LIMITS[tier]
  const isUnlimited = tier === 'unlimited'
  const isEstate = tier === 'max'

  const features: TierFeature[] = []

  // Properties
  if (isUnlimited) {
    features.push({ text: 'Unlimited properties' })
  } else if (limits.maxProperties > 1) {
    features.push({ text: `${limits.maxProperties} properties` })
  } else {
    features.push({ text: `${limits.maxProperties} property` })
  }

  // Entities: what's counted is named, and the tooltip says what isn't.
  const entitiesInfo = UTILITIES_ENABLED
    ? "Completed maintenance and utility bills don't count."
    : "Completed maintenance doesn't count."
  if (isUnlimited) {
    features.push({
      text: UTILITIES_ENABLED ? 'Unlimited items, parts, tasks, and utilities' : 'Unlimited items, parts, and tasks',
    })
  } else {
    features.push({
      text: `${limits.maxEntities.toLocaleString()} ${COUNTED_ENTITIES_LABEL}`,
      info: entitiesInfo,
    })
  }

  // Storage
  if (isUnlimited) {
    features.push({ text: 'Unlimited storage' })
  } else {
    features.push({ text: `${formatStorageSize(limits.maxStorageBytes)} storage` })
  }

  // AI-powered features — all use qualitative chips (trial / limited / unlimited)
  const { aiMessages, invoiceScanner, itemDetection } = limits.features

  if (aiMessages) {
    features.push({ text: 'AI messages', chip: aiAccessChip(aiMessages) })
  }

  if (invoiceScanner && UTILITIES_ENABLED) {
    features.push({ text: 'Bill scanner', chip: aiAccessChip(invoiceScanner) })
  }

  if (itemDetection) {
    features.push({ text: 'AI item detection', chip: aiAccessChip(itemDetection) })
  }

  return features
}

/** Get the description for a tier */
export function getTierDescription(tier: SubscriptionTier): string {
  const descriptions: Record<SubscriptionTier, string> = {
    free: 'Get started managing your home with the basics: track items, maintenance, and warranties.',
    pro: 'For homeowners who want full control over their home inventory and maintenance.',
    max: 'For the serious homeowner with extensive inventory and heavy usage needs.',
    unlimited: 'For power users with large inventories who need maximum capacity and support.',
  }
  return descriptions[tier]
}
